import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  CardContent,
  Stack,
  Avatar,
  styled,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  IconButton,
  Link,
} from "@mui/material";
import {
  IAdminUser,
  UserModalProps,
} from "../../../models/admin/userManagement/userManagement";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { UserModalStyles } from "./UserModalStyles";
import { CloseOutlined } from "@mui/icons-material";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const UserModal: React.FC<UserModalProps> = ({
  user,
  onSave,
  onClose,
  open,
}) => {
  const [formData, setFormData] = useState({
    displayName: user ? user?.displayName : "",
    email: user ? user?.email : "",
    status: user ? user?.status : "INACTIVE",
  });

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = () => {
    // Validate email field
    if (user && !formData.email) {
      alert("Email is mandatory.");
      return;
    }

    const completeUserData: IAdminUser = {
      displayName: formData.displayName,
      email: formData.email,
      status: formData.status,
    };

    onSave(completeUserData);
  };

  useEffect(() => {
    if (user) {
      setFormData({
        displayName: user?.displayName,
        email: user?.email,
        status: user?.status,
      });
    } else {
      setFormData({
        displayName: "",
        email: "",
        status: "INACTIVE",
      });
    }
  }, [user]);

  const handleInviteOrClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      PaperProps={{ style: { width: "400px" } }}
      sx={{ backdropFilter: "blur(5px)" }}
      id="user_modal_dialog"
      data-test-id="user_modal_dialog"
      aria-label="User Management Dialog"
    >
      <DialogTitle sx={UserModalStyles?.dialogTitle}>
        <Typography 
          variant="h6"
          id="user_modal_title"
          data-test-id="user_modal_title"
          aria-label={user ? "Edit User" : "Add User"}
        >
          {user ? "Edit User" : "Add User"}
        </Typography>
        <IconButton 
          onClick={onClose}
          id="user_modal_close"
          data-test-id="user_modal_close"
          aria-label="Close Dialog"
        >
          <CloseOutlined />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box>
          <form
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
              paddingTop: "8px",
            }}
            id="user_modal_form"
            data-test-id="user_modal_form"
            aria-label="User Form"
          >
            <Box sx={UserModalStyles?.avatarWrapper}>
              <Box sx={UserModalStyles?.formFieldsWrapper}>
                <TextField
                  disabled={true}
                  name="email"
                  label="Email"
                  value={formData.email}
                  onChange={handleChange}
                  fullWidth
                  id="user_modal_email"
                  data-test-id="user_modal_email"
                  aria-label="User Email"
                />
                {formData?.status &&
                  formData?.status?.toLowerCase() !== "invited" && (
                    <TextField
                      name="displayName"
                      label="Display Name"
                      value={formData.displayName}
                      onChange={handleChange}
                      fullWidth
                      id="user_modal_display_name"
                      data-test-id="user_modal_display_name"
                      aria-label="User Display Name"
                    />
                  )}
                {formData?.status &&
                  formData?.status?.toLowerCase() !== "invited" && (
                    <FormControl variant="outlined">
                      <InputLabel id="status-select-label">Status</InputLabel>
                      <Select
                        labelId="status-select-label"
                        label="Status"
                        name="status"
                        value={
                          formData.status
                            ? formData?.status?.toUpperCase()
                            : "INACTIVE"
                        }
                        onChange={handleChange}
                        id="user_modal_status"
                        data-test-id="user_modal_status"
                        aria-label="User Status"
                      >
                        <MenuItem value="ACTIVE">Active</MenuItem>
                        <MenuItem value="INACTIVE">Inactive</MenuItem>
                      </Select>
                    </FormControl>
                  )}
              </Box>
            </Box>
          </form>

          <Box sx={UserModalStyles?.formButtonsWrapper}>
            {formData?.status &&
              formData?.status?.toLowerCase() === "invited" && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    // gap: 3,
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                  id="user_modal_invite_actions"
                  data-test-id="user_modal_invite_actions"
                  aria-label="Invite Actions"
                >
                  <Button
                    variant="contained"
                    sx={{ width: "max-content", borderRadius: "4px" }}
                    color="info"
                    onClick={handleInviteOrClose}
                    disabled={true}
                    id="user_modal_resend_invite"
                    data-test-id="user_modal_resend_invite"
                    aria-label="Resend Invite"
                  >
                    Resend Invite
                  </Button>
                  <Button
                    variant="contained"
                    sx={{ width: "max-content", borderRadius: "4px" }}
                    color="error"
                    onClick={handleInviteOrClose}
                    disabled={true}
                    id="user_modal_cancel_invite"
                    data-test-id="user_modal_cancel_invite"
                    aria-label="Cancel Invite"
                  >
                    Cancel Invite
                  </Button>
                </Box>
              )}
            {formData?.status &&
              formData?.status?.toLowerCase() !== "invited" && (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  sx={{ width: "max-content", borderRadius: "4px" }}
                  id="user_modal_save"
                  data-test-id="user_modal_save"
                  aria-label="Save User Changes"
                >
                  Save
                </Button>
              )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default UserModal;
